import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { TypeCode } from '@mindtype/shared'

interface ColorConfig {
  primary: string
  secondary: string
  text: string
}

interface InterpersonalModuleProps {
  userType: TypeCode
  colorConfig?: ColorConfig
}

const DEFAULT_COLOR = { primary: '#3b82f6', secondary: '#60a5fa', text: '#ffffff' }

// 社交风格数据
interface SocialStyleData {
  overview: string
  dimensionAnalysis: string
  roleInTeam: string
}

// 生成社交风格描述
function getSocialStyle(typeCode: string): SocialStyleData {
  const [d1, d2, d3, d4] = typeCode.split('')

  // 互动偏好描述
  let interactionPref = ''
  if (d1 === 'A' && d3 === 'I') {
    interactionPref = '你倾向于深度交流而非广泛社交。在关系建立初期，你可能显得较为保留，但一旦建立信任，你能展现出真诚和深度。'
  } else if (d1 === 'A' && d3 === 'E') {
    interactionPref = '你有自己的社交节奏，既能独立思考，也享受与他人交流带来的成就感。你倾向于选择性地投入关系，在意的关系上会付出更多。'
  } else if (d1 === 'R' && d3 === 'I') {
    interactionPref = '你重视他人的反馈，但社交的动力更多来自内在兴趣而非外在需求。你在感兴趣的话题上能建立深入的连接。'
  } else {
    interactionPref = '你享受社交互动，对他人的反馈敏感，能够灵活地适应不同的社交场合。你的社交范围可能较广，善于维护多样的人际关系。'
  }

  // 维度分析
  const dimParts: string[] = []

  if (d1 === 'A') {
    dimParts.push('你依赖内在标准来判断关系（A），不太容易被社交压力左右')
  } else {
    dimParts.push('你参考外部反馈来调整社交方式（R），善于读取他人的信号')
  }

  if (d2 === 'S') {
    dimParts.push('你用系统化的方式理解他人（S），喜欢观察和分析人的行为模式')
  } else {
    dimParts.push('你用直觉和情境来理解他人（C），善于捕捉氛围和情绪变化')
  }

  if (d3 === 'I') {
    dimParts.push('内在驱动（I）让你更看重关系的质量和意义，而非数量和热闹')
  } else {
    dimParts.push('外在驱动（E）让你重视社交带来的实际价值和认可')
  }

  if (d4 === 'O') {
    dimParts.push('探索取向（O）让你对不同类型的人保持好奇，愿意理解与自己不同的视角')
  } else {
    dimParts.push('闭合取向（D）让你倾向于建立稳定清晰的关系，对模糊的关系状态可能感到不适')
  }

  const dimensionAnalysis = dimParts.join('。') + '。'

  // 团队角色
  let roleInTeam = ''
  if (d1 === 'A' && d3 === 'I') {
    roleInTeam = '在团队中，你更可能是「安静的观察者」或「深度思考的贡献者」，而非「活跃的组织者」。'
  } else if (d1 === 'A' && d3 === 'E') {
    roleInTeam = '在团队中，你可能是「独立的推动者」，有自己的判断但也关注成果，善于在关键时刻发挥影响力。'
  } else if (d1 === 'R' && d3 === 'I') {
    roleInTeam = '在团队中，你可能是「协调性的思考者」，善于整合他人意见，同时保持对问题本身的兴趣。'
  } else if (d1 === 'R' && d3 === 'E') {
    roleInTeam = '在团队中，你可能是「活跃的协作者」或「善于推动的执行者」，在互动和协作中展现能量。'
  } else if (d2 === 'S') {
    roleInTeam = '在团队中，你可能是「条理清晰的分析者」，善于将复杂问题结构化。'
  } else {
    roleInTeam = '在团队中，你可能是「灵活的应变者」，善于处理突发情况和人际关系。'
  }

  return {
    overview: interactionPref,
    dimensionAnalysis,
    roleInTeam,
  }
}

// 类型兼容性分析
interface TypeCompatibility {
  type: TypeCode
  reason: string
}

interface CompatibilityAnalysis {
  natural: TypeCompatibility[]  // 天然契合
  complementary: TypeCompatibility[]  // 互补搭配
  needsAdjustment: TypeCompatibility[]  // 需要磨合
}

function getCompatibilityAnalysis(userType: string): CompatibilityAnalysis {
  const [d1, , , d4] = userType.split('')
  const result: CompatibilityAnalysis = {
    natural: [],
    complementary: [],
    needsAdjustment: [],
  }

  // 天然契合：核心维度相同或相近的类型（0-1个维度不同）
  // 找出与用户只有1个维度不同的类型，并解释原因
  const allTypes: TypeCode[] = [
    'ASID', 'ASIO', 'ASED', 'ASEO',
    'ACID', 'ACIO', 'ACED', 'ACEO',
    'RSID', 'RSIO', 'RSED', 'RSEO',
    'RCID', 'RCIO', 'RCED', 'RCEO',
  ]

  allTypes.forEach(type => {
    if (type === userType) return

    let diff = 0
    const diffDims: number[] = []
    for (let i = 0; i < 4; i++) {
      if (type[i] !== userType[i]) {
        diff++
        diffDims.push(i)
      }
    }

    if (diff === 1) {
      // 天然契合
      const diffDim = diffDims[0]
      let reason = ''

      if (diffDim === 0) {
        reason = `你们在处理模式、驱动来源和不确定性态度上一致，只是锚定来源不同。这种差异通常不影响基本的相互理解。`
      } else if (diffDim === 1) {
        reason = `你们在锚定来源、驱动来源和不确定性态度上一致，只是处理问题的方式略有不同。`
      } else if (diffDim === 2) {
        reason = `你们在认知框架上非常相似，只是动力来源有所不同，这往往能相互补充。`
      } else {
        reason = `你们在核心认知方式上相似，只是对不确定性的态度不同，容易建立默契。`
      }

      result.natural.push({ type, reason })
    } else if (diff === 2) {
      // 互补搭配：2个维度不同，可能形成互补
      // 选择性地添加互补类型
      const hasComplementary = (
        (diffDims.includes(0) && diffDims.includes(3)) || // A/R + D/O 互补
        (diffDims.includes(1) && diffDims.includes(2)) || // S/C + I/E 互补
        (diffDims.includes(0) && diffDims.includes(2))    // A/R + I/E 互补
      )

      if (hasComplementary && result.complementary.length < 3) {
        let reason = ''
        if (diffDims.includes(0) && diffDims.includes(3)) {
          if (d1 === 'A') {
            reason = `TA的外部锚定和${d4 === 'O' ? '闭合' : '探索'}取向可以帮助你落地想法、获得反馈；你的内部锚定和${d4}取向可以帮助TA拓展视角。`
          } else {
            reason = `你们在锚定来源和不确定性态度上互补，能够在决策和执行上形成协作。`
          }
        } else if (diffDims.includes(1) && diffDims.includes(2)) {
          reason = `你们在处理方式和驱动来源上互补，一个偏系统分析一个偏灵活应变，能在项目中形成配合。`
        } else {
          reason = `你们的差异能够形成能力互补，在协作中各自发挥优势。`
        }
        result.complementary.push({ type, reason })
      }
    } else if (diff >= 3) {
      // 需要磨合：3-4个维度不同
      if (result.needsAdjustment.length < 3) {
        let reason = ''
        if (diff === 4) {
          reason = `你们在所有维度上都不同，认知风格差异较大。TA可能觉得你「${d1 === 'A' ? '想太多、不够灵活' : '缺乏主见'}」，你可能觉得TA「${d1 === 'A' ? '太在意外界、缺乏深度' : '固执己见、不听反馈'}」。理解这是认知风格差异而非对错，是磨合的起点。`
        } else {
          const sameIdx = [0, 1, 2, 3].find(i => !diffDims.includes(i))
          const sameDimName = ['锚定来源', '处理模式', '驱动来源', '不确定性态度'][sameIdx!]
          reason = `你们只在${sameDimName}上一致，其他方面差异较大。这意味着你们可能在「如何做事」和「为什么做事」上有不同的理解，需要更多的沟通来避免误解。`
        }
        result.needsAdjustment.push({ type, reason })
      }
    }
  })

  // 确保每个类别至少有2个
  if (result.natural.length > 3) result.natural = result.natural.slice(0, 3)
  if (result.complementary.length > 3) result.complementary = result.complementary.slice(0, 3)
  if (result.needsAdjustment.length > 3) result.needsAdjustment = result.needsAdjustment.slice(0, 3)

  return result
}

// 沟通建议
interface CommunicationTips {
  helpOthersUnderstandYou: string[]
  understandOthers: string[]
}

function getCommunicationTips(typeCode: string): CommunicationTips {
  const [d1, d2, d3, d4] = typeCode.split('')
  const helpOthersUnderstandYou: string[] = []
  const understandOthers: string[] = []

  // 基于 A/R
  if (d1 === 'A') {
    helpOthersUnderstandYou.push(
      '你的思考过程往往在内部完成，别人只看到结论。尝试适当分享你的推理过程，帮助他人理解你的判断依据。'
    )
    understandOthers.push(
      '外部锚定（R）的人需要更多反馈和回应。对你来说自然的沉默思考，可能让TA感到不安。适时给予回应，即使只是「我在想」。'
    )
  } else {
    helpOthersUnderstandYou.push(
      '你重视反馈和协作，但这可能被误解为「没有自己的想法」。在表达观点时，可以先说明你的判断，再询问他人意见。'
    )
    understandOthers.push(
      '内部锚定（A）的人可能不会主动寻求你的意见，这不代表他们不重视你。给他们空间，当他们需要时会来找你。'
    )
  }

  // 基于 S/C
  if (d2 === 'S') {
    helpOthersUnderstandYou.push(
      '你习惯用结构化的方式表达，但有些人更喜欢直接听结论。可以先给要点，需要时再展开细节。'
    )
  } else {
    understandOthers.push(
      '系统化（S）的人需要逻辑和结构。与他们沟通时，尝试组织一下你的想法，按顺序表达会更有效。'
    )
  }

  // 基于 I/E
  if (d3 === 'I') {
    understandOthers.push(
      '外在驱动（E）的人关注结果和认可。当你对某事有内在热情但无法解释「有什么用」时，TA可能难以理解。试着用TA能理解的方式说明价值。'
    )
  } else {
    helpOthersUnderstandYou.push(
      '你关注成果和回报，这可能被误解为「功利」。可以适当表达你对事情本身的兴趣，让他人看到你的投入不只是为了结果。'
    )
  }

  // 基于 D/O
  if (d4 === 'O') {
    helpOthersUnderstandYou.push(
      '你对探索过程的重视可能被误解为「没有结论」或「犹豫不决」。在需要明确表态的场合，可以先给出当前判断，再说明你仍在探索的部分。'
    )
    understandOthers.push(
      '闭合取向（D）的人需要确定性。当你享受开放讨论时，注意TA可能需要一个明确的结论或下一步行动。'
    )
  } else {
    understandOthers.push(
      '探索取向（O）的人享受开放的可能性。当你已经有了结论想推进时，给TA一些探索的空间，急于关闭讨论可能让TA感到被限制。'
    )
  }

  return {
    helpOthersUnderstandYou: helpOthersUnderstandYou.slice(0, 2),
    understandOthers: understandOthers.slice(0, 2),
  }
}

type CompatibilityTab = 'natural' | 'complementary' | 'needsAdjustment'

export function InterpersonalModule({
  userType,
  colorConfig = DEFAULT_COLOR,
}: InterpersonalModuleProps) {
  const color = colorConfig
  const [activeTab, setActiveTab] = useState<CompatibilityTab>('natural')

  const socialStyle = getSocialStyle(userType)
  const compatibility = getCompatibilityAnalysis(userType)
  const tips = getCommunicationTips(userType)

  // 统一的卡片样式
  const cardStyle = {
    border: `1px solid ${color.primary}15`,
  }

  const cardHeaderStyle = {
    borderBottom: `1px solid ${color.primary}15`,
    backgroundColor: `${color.primary}05`,
  }

  const tabConfig = [
    { id: 'natural' as const, name: '天然契合', data: compatibility.natural },
    { id: 'complementary' as const, name: '互补搭配', data: compatibility.complementary },
    { id: 'needsAdjustment' as const, name: '需要磨合', data: compatibility.needsAdjustment },
  ]

  return (
    <div className="space-y-6">
      {/* ===== 1. 你的社交风格 ===== */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
        <div className="px-6 py-4" style={cardHeaderStyle}>
          <h2 className="text-lg font-semibold" style={{ color: color.primary }}>你的社交风格</h2>
          <p className="text-sm text-gray-500 mt-1">你在人际互动中的典型模式</p>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            {socialStyle.overview}
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            {socialStyle.dimensionAnalysis}
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            {socialStyle.roleInTeam}
          </p>
        </div>
      </div>

      {/* ===== 2. 与不同类型的相处 ===== */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
        <div className="px-6 py-4" style={cardHeaderStyle}>
          <h2 className="text-lg font-semibold" style={{ color: color.primary }}>与不同类型的相处</h2>
          <p className="text-sm text-gray-500 mt-1">了解与不同认知风格的人如何互动</p>
        </div>
        <div className="p-6">
          {/* 标签切换 */}
          <div className="flex gap-2 mb-4">
            {tabConfig.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: activeTab === tab.id ? color.primary : `${color.primary}08`,
                  color: activeTab === tab.id ? color.text : color.primary,
                }}
              >
                {tab.name}
              </button>
            ))}
          </div>

          {/* 标签内容 */}
          <div className="space-y-3">
            {tabConfig.find(t => t.id === activeTab)?.data.map((item, i) => (
              <div
                key={i}
                className="rounded-lg p-4"
                style={{
                  backgroundColor: activeTab === 'needsAdjustment' ? '#f9fafb' : `${color.primary}05`,
                  border: `1px solid ${activeTab === 'needsAdjustment' ? '#e5e7eb' : `${color.primary}15`}`,
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="px-2.5 py-1 rounded text-sm font-mono font-semibold"
                    style={{
                      backgroundColor: activeTab === 'needsAdjustment' ? '#e5e7eb' : `${color.primary}15`,
                      color: activeTab === 'needsAdjustment' ? '#374151' : color.primary,
                    }}
                  >
                    {item.type}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{item.reason}</p>
              </div>
            ))}

            {tabConfig.find(t => t.id === activeTab)?.data.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">暂无数据</p>
            )}
          </div>

          {/* 说明文字 */}
          <p className="text-xs text-gray-400 mt-4">
            * 类型兼容性仅供参考。实际关系质量还受到很多其他因素影响，差异不等于冲突。
          </p>
        </div>
      </div>

      {/* ===== 3. 沟通建议 ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 让别人理解你 */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
          <div className="px-6 py-4" style={{ ...cardHeaderStyle, backgroundColor: `${color.primary}08` }}>
            <h3 className="text-base font-semibold" style={{ color: color.primary }}>让别人更好地理解你</h3>
          </div>
          <div className="p-6">
            <ul className="space-y-4">
              {tips.helpOthersUnderstandYou.map((tip, i) => (
                <li key={i} className="text-sm text-gray-700 leading-relaxed flex items-start gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${color.primary}15`, color: color.primary }}
                  >
                    {i + 1}
                  </span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 理解别人 */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
          <div className="px-6 py-4" style={{ ...cardHeaderStyle, backgroundColor: `${color.secondary}10` }}>
            <h3 className="text-base font-semibold" style={{ color: color.secondary }}>更好地理解别人</h3>
          </div>
          <div className="p-6">
            <ul className="space-y-4">
              {tips.understandOthers.map((tip, i) => (
                <li key={i} className="text-sm text-gray-700 leading-relaxed flex items-start gap-3">
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${color.secondary}15`, color: color.secondary }}
                  >
                    {i + 1}
                  </span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ===== 类型对比入口 ===== */}
      <div
        className="rounded-xl p-6 text-center"
        style={{ backgroundColor: `${color.primary}05`, border: `1px solid ${color.primary}15` }}
      >
        <h3 className="text-base font-semibold mb-2" style={{ color: color.primary }}>
          想看看你和朋友的类型对比？
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          选择两个类型进行对比，了解相处模式与沟通建议
        </p>
        <Link
          to={`/compare?a=${userType}`}
          className="inline-block px-6 py-2.5 rounded-lg font-medium transition-all hover:opacity-90"
          style={{ backgroundColor: color.primary, color: color.text }}
        >
          开始对比
        </Link>
      </div>

    </div>
  )
}
